import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ApiClient } from '../api'
import { useMe } from '../hooks/useMe'
import { Icon } from '../components/Icon'
import type { EventFlag } from '../types'

// Flags — the events team's proactive-issues surface (Claude Help Phase 3). A
// daily job (POST /api/internal/daily-flags) writes flags for upcoming/live
// events: roster gaps, advance-booking drops, imminent events missing a briefing
// or timetable, catering still to confirm. Anyone on the team sees them; admins
// resolve them.

const SEVERITY: Record<string, { bg: string; fg: string; label: string }> = {
  urgent: { bg: '#f8dbdd', fg: '#7a2329', label: 'Urgent' },
  warning: { bg: '#f5edd5', fg: '#9b7f2a', label: 'Attention' },
  info: { bg: '#e0e7ec', fg: '#3d5a6c', label: 'Heads-up' }
}

function daysUntil(iso: string): number {
  const d = new Date(`${iso}T00:00:00Z`).getTime()
  const today = new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00Z').getTime()
  return Math.round((d - today) / 86400000)
}

export function Flags() {
  const { data: me } = useMe()
  const qc = useQueryClient()
  const { data, isLoading, error } = useQuery({ queryKey: ['flags'], queryFn: ApiClient.flags, refetchInterval: 5 * 60_000 })
  const resolveMut = useMutation({
    mutationFn: (id: number) => ApiClient.resolveFlag(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flags'] })
  })

  const flags = data?.flags ?? []

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl mb-1 flex items-center gap-2"><Icon name="flag" size={24} className="text-brass" /> Flags</h1>
        <p className="text-ink-soft text-sm max-w-2xl">
          Issues and concerns spotted across upcoming and running events — roster gaps, booking drops, and imminent events
          missing a briefing, timetable or confirmed catering. Reviewed automatically each day.
        </p>
      </div>

      {isLoading && <div className="text-ink-soft text-sm font-mono">Loading flags…</div>}
      {error && (
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          <div className="font-semibold mb-1">Could not load flags</div>
          <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
        </div>
      )}

      {data && flags.length === 0 && (
        <div className="bg-white border border-line rounded p-10 text-center">
          <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center bg-green-soft/60"><Icon name="check-circle" size={24} className="text-green" /></div>
          <h3 className="font-display text-lg mb-1">Nothing flagged</h3>
          <p className="text-sm text-ink-soft max-w-md mx-auto">No open issues across upcoming events right now.</p>
        </div>
      )}

      <div className="space-y-2">
        {flags.map((f: EventFlag) => {
          const sev = SEVERITY[f.severity] ?? SEVERITY.info
          const dts = daysUntil(f.eventStartDate)
          return (
            <div key={f.id} className="bg-white border border-line rounded p-3 flex items-start gap-3">
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded flex-shrink-0 mt-0.5" style={{ background: sev.bg, color: sev.fg }}>{sev.label}</span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{f.title}</div>
                {f.detail && <div className="text-sm text-ink-soft mt-0.5">{f.detail}</div>}
                <div className="text-xs text-ink-soft mt-1">
                  <Link to={`/events/${f.eventId}`} className="font-semibold text-green hover:underline">{f.eventName}</Link>
                  {dts >= 0 ? ` · in ${dts} day${dts === 1 ? '' : 's'}` : ' · running/past'}
                </div>
              </div>
              {me?.isAdmin && (
                <button
                  type="button"
                  onClick={() => resolveMut.mutate(f.id)}
                  disabled={resolveMut.isPending && resolveMut.variables === f.id}
                  className="px-2.5 py-1 text-xs font-semibold rounded border border-line bg-white hover:bg-parchment transition-colors disabled:opacity-50 flex-shrink-0"
                >
                  {resolveMut.isPending && resolveMut.variables === f.id ? 'Resolving…' : 'Resolve'}
                </button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
